import { supabase, type Json } from '@tickitacka/shared';
import { authService } from '@/features/auth/services/authService';
import { v4 as uuidv4 } from 'uuid';
import { Report, ReportReceiver } from '../types/models';
import {
    CreateReportInput,
    ReportWithDetails,
} from '../types/reportTypes';
import {
    createIndexedSentences,
    regenerateIndexedSentences,
} from '../utils/indexingUtils';


/**
 * 보고서 서비스 - CRUD 및 조회 기능
 */
export const reportService = {
    /**
     * 새로운 보고서를 생성합니다
     * @param params 보고서 생성 파라미터
     * @returns 생성된 보고서
     */
    async createReport(params: CreateReportInput): Promise<Report> {
        try {
            const now = new Date().toISOString();
            const reportId = uuidv4();

            console.log('보고서 생성 시작:', JSON.stringify(params, null, 2));

            // markdown_content를 기반으로 indexed_sentences 생성
            const indexedSentences = createIndexedSentences(
                params.markdown_content || ''
            );

            const { data, error } = await supabase
                .from('report')
                .insert({
                    id: reportId,
                    title: params.title,
                    content: params.content || '',
                    markdown_content: params.markdown_content || '',
                    raw_content: params.raw_content || null,
                    report_type_code: params.report_type_code,
                    register_id: params.register_id,
                    workspace_id: params.workspace_id,
                    owner_id: params.owner_id,
                    owner_type: params.owner_type,
                    is_draft: params.is_draft ?? true,
                    register_date: now,
                    update_date: now,
                    enable: true,
                    start_date: params.start_date || null,
                    end_date: params.end_date || null,
                    indexed_sentences:
                        indexedSentences.length > 0
                            ? (indexedSentences as unknown as Json)
                            : null,
                    indexed_sources: params.indexed_sources || null,
                    team_id: params.team_id || null,
                })
                .select()
                .single();

            if (error) {
                console.error('Supabase 오류 상세:', error);
                throw new Error(`보고서 생성 실패: ${error.message}`);
            }

            if (!data) {
                throw new Error('보고서 생성 시 데이터가 반환되지 않았습니다');
            }

            console.log('보고서 생성 성공:', data.id);
            return data;
        } catch (error) {
            console.error('보고서 생성 실패:', error);
            if (error instanceof Error) {
                throw error;
            } else {
                throw new Error(
                    `알 수 없는 오류로 보고서 생성 실패: ${JSON.stringify(error)}`
                );
            }
        }
    },

    /**
     * ID로 보고서를 조회합니다
     * @param reportId 보고서 ID
     * @returns 보고서 상세 정보
     */
    async getReportById(reportId: string): Promise<ReportWithDetails | null> {
        try {
            const { data, error } = await supabase
                .from('report')
                .select(
                    `
                    *,
                    report_type:report_type_code (
                        code, name, description
                    ),
                    receivers:report_receiver!report_id (
                        *,
                        user:user_id (
                            id, name, profile_image_url
                        )
                    ),
                    creator:register_id (
                        id, name, profile_image_url
                    )
                    `
                )
                .eq('id', reportId)
                .single();

            if (error) throw error;
            return data as unknown as ReportWithDetails;
        } catch (error) {
            console.error('보고서 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 보고서를 업데이트합니다
     * @param reportId 보고서 ID
     * @param updates 업데이트할 필드들
     * @returns 업데이트된 보고서
     */
    async updateReport(
        reportId: string,
        updates: Partial<Report>
    ): Promise<Report> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const updateData = {
                ...updates,
                update_id: user.id,
                update_date: new Date().toISOString(),
            } as Partial<Report>;

            // markdown_content가 업데이트되면 indexed_sentences도 재생성
            if (
                'markdown_content' in updateData &&
                updateData.markdown_content !== undefined &&
                typeof updateData.markdown_content === 'string'
            ) {
                const indexedSentences = regenerateIndexedSentences(
                    updateData.markdown_content
                );
                const dataWithIndexed = updateData as Record<string, unknown>;
                dataWithIndexed.indexed_sentences =
                    indexedSentences.length > 0
                        ? (indexedSentences as unknown as Json)
                        : null;
            }

            const { data, error } = await supabase
                .from('report')
                .update(updateData)
                .eq('id', reportId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('보고서 업데이트 실패:', error);
            throw error;
        }
    },

    /**
     * 보고서 내용을 업데이트합니다
     * @param reportId 보고서 ID
     * @param content 새로운 내용
     * @param markdownContent 마크다운 내용
     * @param updates 추가 업데이트 필드
     * @returns 업데이트된 보고서
     */
    async updateReportContent(
        reportId: string,
        content: string,
        markdownContent?: string,
        updates?: Partial<Report>
    ): Promise<Report> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const updateData = {
                ...updates,
                content,
                update_id: user.id,
                update_date: new Date().toISOString(),
            } as Record<string, unknown>;

            // markdown_content가 제공된 경우에만 추가
            if (markdownContent !== undefined) {
                updateData.markdown_content = markdownContent;
                // markdown_content가 업데이트되면 indexed_sentences도 재생성
                const indexedSentences =
                    regenerateIndexedSentences(markdownContent);
                updateData.indexed_sentences =
                    indexedSentences.length > 0
                        ? (indexedSentences as unknown as Json)
                        : null;
            }

            const { data, error } = await supabase
                .from('report')
                .update(updateData)
                .eq('id', reportId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('보고서 내용 업데이트 실패:', error);
            throw error;
        }
    },

    /**
     * 보낸 보고서를 소프트 삭제합니다 (enable을 false로 변경)
     * @param reportId 보고서 ID
     * @returns 삭제된 보고서
     */
    async deleteReport(reportId: string): Promise<Report> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('report')
                .update({
                    enable: false,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', reportId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('보고서 소프트 삭제 실패:', error);
            throw error;
        }
    },

    /**
     * 받은 보고서를 소프트 삭제합니다 (enable을 false로 변경)
     * @param reportId 보고서 ID
     * @returns 삭제된 보고서 수신자 레코드
     */
    async deleteReceivedReport(reportId: string): Promise<ReportReceiver> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('report_receiver')
                .update({
                    enable: false,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('report_id', reportId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('보고서 소프트 삭제 실패:', error);
            throw error;
        }
    },

    /**
     * 사용자가 작성한 보고서 목록을 수신자 정보와 함께 조회합니다
     * @param userId 사용자 ID
     * @param workspaceId 워크스페이스 ID (선택사항)
     * @param searchQuery 검색어 (선택사항)
     * @returns 보고서 목록
     */
    async getReportsByUserIdWithReceivers(
        userId: string,
        workspaceId?: string,
        searchQuery?: string
    ): Promise<ReportWithDetails[]> {
        try {
            let query = supabase
                .from('report')
                .select(
                    `
                    *,
                    report_type:report_type_code (
                        code, name, description
                    ),
                    receivers:report_receiver (
                        *,
                        user:user_id (
                            id, name, profile_image_url
                        )
                    ),
                    creator:register_id (
                        id, name, profile_image_url
                    )
                    `
                )
                .eq('register_id', userId)
                .eq('enable', true)
                .is('team_id', null) // 개인 보고서만 조회 (team_id가 없는 것)
                .order('register_date', { ascending: false });

            if (workspaceId) {
                query = query.eq('workspace_id', workspaceId);
            }
            
            // 검색어가 있으면 제목으로 필터링
            if (searchQuery?.trim()) {
                query = query.ilike('title', `%${searchQuery.trim()}%`);
            }

            const { data, error } = await query;

            if (error) throw error;
            return (data || []) as unknown as ReportWithDetails[];
        } catch (error) {
            console.error('수신자 정보 포함 사용자별 보고서 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 사용자가 수신한 보고서 목록을 수신자 정보와 함께 조회합니다
     * @param userId 사용자 ID
     * @param workspaceId 워크스페이스 ID (선택사항)
     * @param searchQuery 검색어 (선택사항)
     * @returns 보고서 목록
     */
    async getReportByReceiverId(
        userId: string,
        workspaceId?: string,
        searchQuery?: string
    ): Promise<ReportWithDetails[]> {
        try {
            let query = supabase
                .from('report')
                .select(
                    `
                    *,
                    report_type:report_type_code (
                        code, name, description
                    ),
                    receivers:report_receiver!inner (
                        *,
                        user:user_id (
                            id, name, profile_image_url
                        )
                    ),
                    creator:register_id (
                        id, name, profile_image_url
                    )
                    `
                )
                .eq('receivers.user_id', userId)
                .eq('receivers.enable', true)
                .is('team_id', null); // 개인 보고서만 조회 (team_id가 없는 것)

            // 워크스페이스 ID가 제공된 경우 해당 워크스페이스의 보고서만 필터링
            if (workspaceId) {
                query = query.eq('workspace_id', workspaceId);
            }
            
            // 검색어가 있으면 제목으로 필터링
            if (searchQuery?.trim()) {
                query = query.ilike('title', `%${searchQuery.trim()}%`);
            }

            const { data: reportData, error: reportError } = await query.order(
                'update_date',
                { ascending: false }
            );

            if (reportError) throw reportError;

            return (reportData || []) as unknown as ReportWithDetails[];
        } catch (error) {
            console.error('수신자 정보 포함 수신자별 보고서 조회 실패:', error);
            throw error;
        }
    }
};
